import ContextMenu from '@/components/common/ContextMenu';
import InstrumentInfoContext from '@/components/common/InstrumentInfoContext';
import TabFilters from '@/components/common/TabFilters';
import SafeInput from '@/components/inputs/SafeInput';
import ClosePositionModal from '@/components/modal/ClosePositionModal';
import VirtualizedTable from '@/components/table/VirtualizedTable';
import { Modify } from '@/model/common';
import { PairMap } from '@/state/reducers/marketPairSlice';
import { OpenPosition } from '@/state/reducers/openPositionSlice';
import { OrderPrefillValues } from '@/state/reducers/orderSlice';
import useContextMenu from '@/utils/hooks/useContextMenu';
import { UseDisclosureReturn } from '@/utils/hooks/useDisclosure';
import useOpenPositionsTable from '@/utils/hooks/useOpenPositionTable';
import { Fragment, memo, useState } from 'react';
import { MdSearch } from 'react-icons/md';
import PositionGroupSwitch, { GroupBy } from './PositionGroupSwitch';

export interface OpenPositionRow
    extends Modify<
        OpenPosition,
        {
            unrealisedPnl: {
                value: number;
                currency: string;
            };
            realisedPnl: {
                value: number;
                currency: string;
            };
            instrument: string;
            tableInstrument: string;
            market: PairMap;
            type: string;
            netQtyPosition: number;
            openPrice: number;
            currentPrice: number;
            settlementDate: string;
            accountCode: string;
            realisedPnLCurrency: string;
            tsSymbol?: string;
        }
    > {}

export interface OpenPositionTableProps {
    setCSVData: React.Dispatch<React.SetStateAction<any[]>>;
    onOrderModal: (row: OpenPositionRow) => void;
    prefillValues?: OrderPrefillValues;
    setPrefillValues?: React.Dispatch<React.SetStateAction<OrderPrefillValues | undefined>>;
    orderDisclosure: UseDisclosureReturn;
}

export type PositionFilter = 'Show All' | 'Hide Closed Positions';

const positionFilters: { label: PositionFilter; value: boolean }[] = [
    { label: 'Show All', value: true },
    { label: 'Hide Closed Positions', value: false }
];

const OpenPositionTable = (props: OpenPositionTableProps) => {
    const { prefillValues, setPrefillValues, orderDisclosure } = props;

    const contextMenu = useContextMenu();

    const { contextMenuX, contextMenuY, contextMenuVisible, handleOpenContextMenu, handleCloseContextMenu } =
        contextMenu;

    const [groupBy, setGroupBy] = useState<GroupBy>('Products');
    const [positionFilter, setPositionFilter] = useState<boolean>(true);
    const [instrumentInfoContext, setInstrumentInfoContext] = useState();

    const { debouncedTableInstance, debouncedOpenPositions, expandedRows, globalFilter } = useOpenPositionsTable({
        ...props,
        groupBy,
        positionFilter,
        setInstrumentInfoContext,
        handleOpenContextMenu
    });

    return (
        <Fragment>
            <div className="flex flex-col h-full gap-2">
                <div className="flex items-center justify-between gap-2 flex-wrap-reverse">
                    <div className="flex gap-2 whitespace-nowrap">
                        <TabFilters.Common
                            filters={positionFilters}
                            selectedFilter={positionFilter}
                            setSelectedFilter={setPositionFilter}
                        />
                        <div className="relative flex items-center">
                            <MdSearch className="absolute left-2 text-brand-neutral-400 z-10 w-5 h-5" />
                            <SafeInput
                                className="w-48 text-xs rounded-none pl-8 py-1.5 !h-8"
                                placeholder="Search..."
                                value={globalFilter || ''}
                                onChange={(e) => debouncedTableInstance.setGlobalFilter(e.target.value)}
                            />
                        </div>
                    </div>
                    <div>
                        <PositionGroupSwitch groupBy={groupBy} setGroupBy={setGroupBy} />
                    </div>
                </div>
                <div className="flex-1 basis-0 overflow-hidden">
                    <MemoizedOpenPositionTable
                        groupBy={groupBy}
                        expandedRows={expandedRows}
                        globalFilter={globalFilter}
                        positaionFilter={positionFilter}
                        tableInstance={debouncedTableInstance}
                        debouncedOpenPositions={debouncedOpenPositions}
                    />
                </div>
            </div>
            <ContextMenu
                x={contextMenuX}
                y={contextMenuY}
                offset={{ x: 4, y: 4 }}
                visible={contextMenuVisible}
                onClose={handleCloseContextMenu}
                className="!bg-brand-background !w-auto">
                <InstrumentInfoContext market={instrumentInfoContext} handleCloseContextMenu={handleCloseContextMenu} />
            </ContextMenu>
            <ClosePositionModal
                debouncedOpenPositions={debouncedOpenPositions}
                prefillValues={prefillValues}
                setPrefillValues={setPrefillValues}
                opened={orderDisclosure[0]}
                handlers={orderDisclosure[1]}
            />
        </Fragment>
    );
};

export default OpenPositionTable;

const MemoizedOpenPositionTable = memo(({ tableInstance, debouncedOpenPositions, expandedRows, globalFilter }: any) => {
    // globalFilterEnabled
    return <VirtualizedTable tableInstance={tableInstance} isLoadingTableContent={false} />;
});

MemoizedOpenPositionTable.displayName = 'OpenPositionTable';
